import { Component, Inject, OnInit, Optional, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { DialogDataService } from '@views/pages/apps/settings/announcements/dialog/services/dialog-data.service';
import { MessageTemplateDataService } from '@views/pages/apps/superuser/message-template/services/message-template-data.service';
import { EventEmitterService } from '@core/services/event-emitter.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'kt-promotion',
  templateUrl: './message-setting.component.html',
  styleUrls: ['./message-setting.component.scss']
})
export class MessageSettingComponent implements OnInit {
  form: FormGroup;
  messageTemplatesSettings = {
    maxHeight: 200,
    singleSelection: true,
    text: 'Please Select',
    enableFilterSelectAll: false,
    enableSearchFilter: true,
    classes: 'dropdown',
    primaryKey: 'id',
    labelKey: 'code',
    showCheckbox: false,
    disabled: false
  };
  messageTemplateList = [];
  messageTemplateSelectedItem = [];
  smsTemplatesSettings = {
    maxHeight: 200,
    singleSelection: true,
    text: 'Please Select',
    enableFilterSelectAll: false,
    enableSearchFilter: true,
    classes: 'dropdown',
    primaryKey: 'id',
    labelKey: 'code',
    showCheckbox: false,
    disabled: false
  };
  smsTemplateList = [];
  smsTemplateSelectedItem = [];
  popupDropdownSettings = {
    autoPosition: false,
    singleSelection: true,
    text: 'Please Select',
    enableFilterSelectAll: false,
    enableSearchFilter: true,
    classes: 'dropdown',
    maxHeight: 200,
    primaryKey: 'id',
    labelKey: 'labelKey',
    noDataLabel: '',
    showCheckbox: false,
    disabled: false
  };
  popupDropdownList = [];
  popupDropdownListArray = [];
  popupSelectedItems = [];
  dropdown = {
    smstemplates: this.messageTemplateDataService.getMessageTemplateList(2, 8),
    messagetemplates: this.messageTemplateDataService.getMessageTemplateList(1, 8),
  };

  // Dropdown loading flags — the three lists are fetched one after another in ngOnInit, so all
  // three controls start disabled with a spinner and each is released as its own list arrives.
  messageTemplateLoading = false;
  smsTemplateLoading = false;
  popupLoading = false;

  private formSubscription: Subscription;
  private saveSubscription: Subscription;

  constructor(
    private messageTemplateDataService: MessageTemplateDataService,
    private dialogDataService: DialogDataService,
    @Optional() @Inject('initialData') private initialData: any,
    private eventEmitterService: EventEmitterService,
    private cdr: ChangeDetectorRef,
  ) {
    this.initialData = initialData;
  }

  async ngOnInit() {
    this.formInit();
    this.eventEmitterService.onMessageSettingData(this.form.value);
    this.formSubscription = this.form.valueChanges.subscribe(values => {
      this.eventEmitterService.onMessageSettingData(values);
    });
    this.startDropdownLoading();
    await this.setSMSDropdown();
    await this.setMessagesDropdown();
    await this.setPopups();
    setTimeout(() => {
      this.messageTemplateSelectedItem = [...this.messageTemplateSelectedItem];
      this.smsTemplateSelectedItem = [...this.smsTemplateSelectedItem];
      this.popupSelectedItems = [...this.popupSelectedItems];
      this.cdr.detectChanges(); 
    });
  }

  private formInit() {
    let smsTemplate = this.initialData?.message_template_sms_id ? (this.initialData.message_template_sms_id === 0 ? null : this.initialData.message_template_sms_id) : null;
    let messageTemplate = this.initialData?.message_template_id ? (this.initialData.message_template_id === 0 ? null : this.initialData.message_template_id) : null;
    let dialogPopup = this.initialData?.dialog_popups || null;

    this.form = new FormGroup({
      message_template_sms_id: new FormControl(smsTemplate),
      message_template_id: new FormControl(messageTemplate),
      dialog_popups: new FormControl(dialogPopup),
    });
  }

  ngOnDestroy() {
    if (this.formSubscription) {
      this.formSubscription.unsubscribe();
    }
    if (this.saveSubscription) {
      this.saveSubscription.unsubscribe();
    }
  }

  private startDropdownLoading() {
    this.smsTemplateLoading = true;
    this.smsTemplatesSettings = { ...this.smsTemplatesSettings, disabled: true };
    this.messageTemplateLoading = true;
    this.messageTemplatesSettings = { ...this.messageTemplatesSettings, disabled: true };
    this.popupLoading = true;
    this.popupDropdownSettings = { ...this.popupDropdownSettings, disabled: true };
  }

  private setMessagesDropdown():Promise<void> {
    let messageTemplate = this.initialData?.message_template_id || null;

    const stopLoading = () => {
      this.messageTemplateLoading = false;
      this.messageTemplatesSettings = { ...this.messageTemplatesSettings, disabled: false };
      this.cdr.detectChanges();
    };

    return new Promise<void>((resolve, reject) => {
      this.dropdown.messagetemplates.subscribe({
        next: res => {
          this.messageTemplateList = res;
          if (this.form.value.message_template_id) {
            this.messageTemplateSelectedItem = [...this.messageTemplateList.filter(t => t.id === this.form.value.message_template_id)];
          }
          if (messageTemplate) {
            this.messageTemplateSelectedItem = [...this.messageTemplateList.filter(t => t.id === messageTemplate)];
          }
          stopLoading();
          resolve();
        },
        // The global ApiErrorInterceptor surfaces the error; release the control so it isn't stuck disabled.
        error: () => { stopLoading(); resolve(); },
      });
    });
  }

  private setSMSDropdown():Promise<void> {
    let smsTemplate = this.initialData?.message_template_sms_id || null;

    const stopLoading = () => {
      this.smsTemplateLoading = false;
      this.smsTemplatesSettings = { ...this.smsTemplatesSettings, disabled: false };
      this.cdr.detectChanges();
    };

    return new Promise<void>((resolve, reject) => {
      this.dropdown.smstemplates.subscribe({
        next: res => {
          this.smsTemplateList = res;
          if (this.form.value.message_template_sms_id) {
            this.smsTemplateSelectedItem = [...this.smsTemplateList.filter(t => t.id === this.form.value.message_template_sms_id)];
          }
          if (smsTemplate) {
            this.smsTemplateSelectedItem = [...this.smsTemplateList.filter(t => t.id === smsTemplate)];
          }
          stopLoading();
          resolve();
        },
        error: () => { stopLoading(); resolve(); },
      });
    });
  }

  private setPopups():Promise<void> {
    let dialogPopup = this.initialData?.dialog_popups || null;
    let contents = [];

    const stopLoading = () => {
      this.popupLoading = false;
      this.popupDropdownSettings = { ...this.popupDropdownSettings, disabled: false };
      this.cdr.detectChanges();
    };

    return new Promise<void>((resolve, reject) => {
      this.dialogDataService.getWithQuery(`?paginate=false&status=1`).subscribe({
        next: res => {
        res.map(function (elm) {
          elm.contents.forEach(v => contents.push(v));

          let localeTitle = contents.filter(x => x.title != null && x.popup_id == elm.id);
          localeTitle = localeTitle.sort(function (a, b) {
            return a.locale_id - b.locale_id;
          });

          if (localeTitle.length > 0) {
            let title = (localeTitle[0]['title'].length > 20) ? localeTitle[0]['title'].slice(0, 20 - 1) + ' . . . ' : localeTitle[0]['title'];
            elm['labelKey'] = elm.code + ' (' + title + ')';
          }
          else {
            elm['labelKey'] = elm.code;
          }
        });

        this.popupDropdownList = this.popupDropdownList.concat(res);
        this.popupDropdownList = this.popupDropdownList.map((x) => {
          return {
            id: x.id,
            start_date: x.start_date,
            end_date: x.end_date,
            promotion_id: null,
            labelKey: x.labelKey,
            code: x.code
          }
        });

        this.popupDropdownListArray = this.popupDropdownList;
        if (dialogPopup) {
          this.popupSelectedItems = [...this.popupDropdownListArray.filter(p => p.id == dialogPopup)];
        }
        stopLoading();
        resolve();
        },
        error: () => { stopLoading(); resolve(); },
      });
    });
  }

  onSelectedPopupItems($event: any) {
    if ($event.length == 0) {
      this.popupSelectedItems = [];
      this.form.patchValue({ dialog_popups: null }, { emitEvent: true });
    }
    else {
      this.popupSelectedItems = [$event[0]];
      this.form.patchValue({ dialog_popups: $event[0].id }, { emitEvent: true });
    }
  }

  checkDialogValidityDate(selectedPopup: any) {
    if (selectedPopup != undefined && selectedPopup.length > 0) {
      var today = new Date();
      var start_date = new Date(selectedPopup[0]['start_date']);

      if (selectedPopup[0]['end_date'] != null) {
        var end_date = new Date(selectedPopup[0]['end_date']);
        return today > end_date || today < start_date;
      } else {
        return today < start_date;
      }
    }
    return false;
  }
}
